'use client'

/**
 * MessageContentRenderer - 消息内容渲染器
 * 根据解析结果渲染不同类型的内容片段
 */

import { useEffect, useMemo } from 'react'

import { transformToMarkdownHtml } from '@/components/utils/Markdown'
import { ensureGlobalStyles } from './globalStyleManager'
import HtmlBoxIframe from './HtmlBoxIframe'
import { parseMessageContent, scopeStyles } from './parseMessageContent'

interface MessageContentRendererProps {
  content: string
  msgId: string
  isUserMessage?: boolean
  characterName?: string
  userName?: string
  className?: string
  id?: string
}

export default function MessageContentRenderer({
  content,
  msgId,
  isUserMessage = false,
  characterName,
  userName,
  className,
  id,
}: MessageContentRendererProps) {
  // 用户消息：复用普通聊天 markdown 解析逻辑后再包裹 <message>
  const processedContent = isUserMessage
    ? `<message>${transformToMarkdownHtml(content).trim()}</message>`
    : content

  const { segments, styles } = useMemo(
    () => parseMessageContent(processedContent, characterName, userName),
    [processedContent, characterName, userName]
  )

  const scopedStyles = useMemo(
    () => scopeStyles(styles, msgId),
    [styles, msgId]
  )

  // 将消息级样式注入全局（持久化，不随组件卸载移除）
  useEffect(() => {
    if (scopedStyles) {
      ensureGlobalStyles(msgId, scopedStyles)
    }
  }, [msgId, scopedStyles])

  // 没有内容和样式时不渲染
  if (segments.length === 0 && !scopedStyles) {
    return null
  }

  return (
    <>
      {/* 消息内容 */}
      {segments.length > 0 && (
        <div
          className={`message-content${className ? ` ${className}` : ''}`}
          data-msg-id={msgId}
          id={id}
        >
          {segments.map((segment, index) => {
            switch (segment.type) {
              case 'message':
                return (
                  <div
                    key={index}
                    dangerouslySetInnerHTML={{ __html: segment.content }}
                    className={
                      isUserMessage
                        ? 'user-message-bubble'
                        : 'char-message-bubble'
                    }
                    id={
                      isUserMessage
                        ? 'tipsy-user-message-bubble'
                        : 'tipsy-char-message-bubble'
                    }
                  />
                )
              case 'narration':
                return (
                  <div
                    key={index}
                    className="narration-segment"
                    dangerouslySetInnerHTML={{ __html: segment.content }}
                    id="tipsy-narration-box"
                  />
                )
              case 'html-box':
                return (
                  <HtmlBoxIframe
                    key={`html-box-${index}-${segment.content.length}-${segment.content.slice(0, 24)}`}
                    content={segment.content}
                  />
                )
              case 'text':
              case 'html':
                return (
                  <div
                    key={index}
                    dangerouslySetInnerHTML={{ __html: segment.content }}
                  />
                )
              default:
                return null
            }
          })}
        </div>
      )}
    </>
  )
}
